import { graphql } from '@api/generated';

import { getClient } from '../../../api/client';
import {
  InteractionClassificationDataMutation,
  InteractionClassificationStatus,
  InteractionStatus,
} from '@api/generated/graphql';
import IAppLogger from '@app/lib/IAppLogger';
import { retryMutation } from '@api/lib/retryMutate';
import { INTERACTION_CLASSIFICATION_DATA_MUTATION } from 'src/constants/operations';

/**
 * Determines whether to retry a mutation based on the classification status.
 * Returns true if the classification status is NotReady, indicating the data
 * is not yet available and should be retried.
 */
export function shouldRetryForClassificationStatus(
  errOrResult: unknown,
): boolean {
  const classificationStatus = (
    errOrResult as InteractionClassificationDataMutation
  )?.syncInteraction?.classificationStatus;
  return classificationStatus === InteractionClassificationStatus.NotReady;
}

const INTERACTION_CLASSIFICATION_MUTATION = graphql(`
  mutation interactionClassificationData($interactionId: ID, $channelId: ID) {
    syncInteraction(interactionId: $interactionId, channelId: $channelId) {
      classificationStatus
      channelDetails {
        completedAt
      }
      status
    }
  }
`);

/**
 * Fetches the classification status and completion data from an interaction.
 * Can accept either interactionId, channelId, or both.
 * Returns an object containing the classification status and completedAt time, or null if not available.
 */
async function getInteractionClassificationData(
  options: { interactionId?: string; channelId?: string },
  logger: IAppLogger,
): Promise<{
  classificationStatus: InteractionClassificationStatus | null;
  completedAt: string | null;
  status: InteractionStatus | null;
} | null> {
  try {
    const { data } = await retryMutation(
      () =>
        getClient().mutate({
          mutation: INTERACTION_CLASSIFICATION_MUTATION,
          variables: {
            interactionId: options.interactionId,
            channelId: options.channelId,
          },
        }),
      {
        operationName: INTERACTION_CLASSIFICATION_DATA_MUTATION,
        shouldRetryForGraphQLErrors: shouldRetryForClassificationStatus,
      },
    );

    return {
      classificationStatus: data?.syncInteraction?.classificationStatus || null,
      completedAt: data?.syncInteraction?.channelDetails?.completedAt || null,
      status: data?.syncInteraction?.status || null,
    };
  } catch (error) {
    logger.error(
      'Unable to fetch interaction classification and completion data',
      error,
    );
    return null;
  }
}

export default getInteractionClassificationData;
